<template>
  <b-row>
    <b-col>
      <div class="all-product-cards">
        <template v-for="(allGroupProducts, index) in sortedProducts">
          <h2
            :key="`grouping-label-${index}`"
            class="product-heading"
          >
            {{ getGroupByLabel(allGroupProducts) }}
          </h2>
          <div
            :key="`grouping-cards-${index}`"
            class="product-cards"
          >
            <div
              v-for="(product, index) in allGroupProducts.sortedProducts"
              :key="index"
              class="product-card"
            >
              <div
                v-if="hasSettings(product)"
                class="settings-icon"
              >
                <button @click="showSubstituteLightBox(product)">
                  <font-awesome-icon
                    :icon="[ 'fa', 'ellipsis-vertical' ]"
                    size="1x"
                    class="icon"
                    fixed-width
                  />
                </button>
              </div>
              <div class="product-image">
                <AImage
                  :img="getFirstImage(product)"
                  ratio="3-4"
                  preset="wineproductteaser"
                  contain
                  @click.native="showDetails(product)"
                />
                <div
                  v-if="product.awardWinner"
                  class="award-winner"
                >
                  Award Winner
                </div>
                <div class="details-icon">
                  <details-icon />
                </div>
              </div>
              <div class="product-info">
                <div class="product-title">
                  <h3>{{ product.title | formatEntities }}</h3>
                </div>
              </div>
              <div class="pricing">
                <div
                  v-if="priceDifferent(product)"
                  class="price sale-price"
                >
                  {{ price(product) }}
                </div>
                <div class="price club-price">
                  {{ discountedPrice(product) }}
                </div>
                <product-debug-info :product="product" />
              </div>
              <div
                :class="{ 'show-selector': getQuantity(product) > 0 }"
                class="quantity-container"
              >
                <div
                  class="quantity-container-inner"
                >
                  <div
                    class="quantity-selector"
                  >
                    <div
                      class="btn btn-primary decrement"
                      @click="decrement(product)"
                    >
                      -
                    </div>
                    <div class="quantity-input">
                      <b-input
                        :key="quantityInputKey"
                        :value="getQuantity(product)"
                        min="0"
                        step="1"
                        type="number"
                        data-cy="bottle-qty"
                        @input="(val) => setQuantity(product, val)"
                        @keypress.native="keyPress"
                      />
                    </div>
                    <div
                      :class="{ 'disabled': maxReached(product) }"
                      class="btn btn-primary increment"
                      @click="increment(product)"
                    >
                      +
                    </div>
                  </div>
                  <div
                    v-if="onRequest(product) && !hasDefaultSelection(product)"
                    class="btn btn-primary on-request"
                    data-cy="on-request"
                    @click="makeRequest(product)"
                  >
                    On Request
                  </div>
                  <div
                    v-else
                    :class="{ 'disabled': maxReached(product) }"
                    class="btn btn-primary add-to-order"
                    data-cy="add-to-order"
                    @click="increment(product)"
                  >
                    Add To Order
                  </div>
                </div>
              </div>
              <transition name="move-in">
                <div
                  v-if="productMaxReached(product)"
                  class="product-max-reached"
                >
                  The maximum quantity for this product has been reached.
                </div>
              </transition>
              <transition name="move-in">
                <div
                  v-if="!productMaxReached(product) && !isVisible(product)"
                  class="product-not-readdable"
                >
                  You have reduced the quantity of a limited product. If you save your order, you will not be able to add more of this product.
                </div>
              </transition>
            </div>
          </div>
        </template>
      </div>
    </b-col>

    <AModal
      v-model="showLightbox"
      :actions="lightboxActions"
      @modalhidden="$emit('modalToggled')"
    >
      <div
        v-if="lightboxProduct"
        class="lightbox-product-details"
      >
        <div class="lightbox-product-title">
          {{ lightboxProduct.title | formatEntities }}
        </div>
        <div class="lightbox-product-image">
          <AImage
            v-if="currentLightboxImage"
            :img="currentLightboxImage"
            contain
          />
        </div>

        <div class="pricing">
          <div
            v-if="priceDifferent(lightboxProduct)"
            class="price sale-price"
            title="Sale Price"
          >
            {{ price(lightboxProduct) }}
          </div>
          <div
            class="price club-price"
            title="Club Price"
          >
            {{ discountedPrice(lightboxProduct) }}
          </div>
        </div>

        <div
          class="lightbox-product-description"
          v-html="lightboxProduct.abstract"
        />
      </div>
    </Amodal>

    <AModal
      v-model="showSubstituteLightbox"
      :actions="substituteLightboxActions"
      @modalhidden="$emit('modalToggled')"
    >
      <div
        v-if="substituteLightboxProduct"
        class="lightbox-product-details"
      >
        <div class="lightbox-product-title">
          {{ substituteLightboxProduct.title | formatEntities }} Substitutes
        </div>
        <div class="description">
          {{ substituteDescription }}
        </div>
        <div>
          <draggable
            v-model="draggableList"
            v-bind="dragOptions"
            class="subtitute-products-drag"
          >
            <div
              v-for="(product) in draggableList"
              :key="product.product.id"
              class="product"
            >
              <div class="product-title">
                <font-awesome-icon
                  :icon="[ 'fa', 'grip-lines' ]"
                  size="1x"
                  class="icon"
                  fixed-width
                />
                {{ product.product.title }}
              </div>
            </div>
          </draggable>
        </div>
      </div>
    </Amodal>
  </b-row>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex'
import Draggable from 'vuedraggable'
import DetailsIcon from '../../svgs/DetailsIcon'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faGripLines, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons'

library.add(faGripLines)
library.add(faEllipsisVertical)

export default {
  name: 'custom-step',
  components: {
    DetailsIcon,
    Draggable
  },
  data() {
    return {
      showLightbox: false,
      lightboxProduct: null,
      showSubstituteLightbox: false,
      substituteLightboxProduct: null,
      currentLightboxImage: null,
      quantityInputKey: 0
    }
  },
  computed: {
    ...mapState('somnambulistui', ['selectedOrderType', 'selectedProducts', 'substituteProducts']),
    ...mapGetters('somnambulistui', ['isFixedQuantity', 'isCustomOrder', 'getSelectedOrderProducts', 'getSelectedQuantityObject', 'customOrderProductCount']),
    substituteDescription() {
      return this.$store.state.registry.getModuleSetting('somnambulist', 'substituteDescription', 'If this product is out of stock, please sort the products below in order of preference to be used as a substitute.')
    },
    draggableList: {
      get() {
        const defaults = Object.values(this.substituteLightboxProduct.substitute_products).sort(({ delta: a }, { delta: b }) => a - b)
        const current = this.substituteProducts[this.substituteLightboxProduct.id] ?? []
        const result = [];
        const added = []; // to track what's already in result

        // First pass: add from current if valid and not already added
        for (const product of current) {
          if (typeof defaults.find(p => p.product.id === product.product.id) !== 'undefined' && !added.includes(product.product.id)) {
            result.push(product);
            added.push(product.product.id);
          }
        }

        // Second pass: add missing defaults in their original order
        for (const product of defaults) {
          if (!added.includes(product.product.id)) {
            result.push(product);
            added.push(product.product.id);
          }
        }

        return result;
      },
      set(value) {
        this.SET_SUBSTITUTE_PRODUCT({ id: this.substituteLightboxProduct.id, products: value })
      }
    },
    dragOptions() {
      return {
        animation: 200,
        disabled: false,
        ghostClass: 'sortable-ghost',
        scrollSensitivity: 300,
        forceFallback: true
      }
    },
    products() {
      return this.getSelectedOrderProducts.filter(product => this.isVisible(product) || this.hasDefaultSelection(product))
    },
    sortedProducts() {
      const productSortingConfig = this.$store.state.registry.getModuleSetting('somnambulist', 'productSorting', {})
      const sortOrder = productSortingConfig.sortOrder
      const sortBy = productSortingConfig.sortBy
      const orderBy = productSortingConfig.orderBy
      const groupBy = productSortingConfig.groupBy
      const order = productSortingConfig.order === 'ASC' ? 1 : -1
      const selectedToTop = productSortingConfig.selectedToTop

      const _sortVal = (prod, val) => {
        if (prod.hasOwnProperty(val)) return prod[val]
        else if (prod.hasOwnProperty('productsettings') && prod.productsettings.hasOwnProperty(val)) return prod.productsettings[val]
        else return null
      }

      const sorted = this.products
        .toSorted((productA, productB) => {
          if (selectedToTop) {
            if (this.hasDefaultSelection(productA) && this.hasDefaultSelection(productB)) return 0
            if (this.hasDefaultSelection(productA)) return order * -1
            if (this.hasDefaultSelection(productB)) return order * 1
          }
          if (productA._somnambulistCatalogOrder && productB._somnambulistCatalogOrder) return order * productA._somnambulistCatalogOrder - productB._somnambulistCatalogOrder
          if (productA._somnambulistCatalogOrder) return order * -1
          if (productB._somnambulistCatalogOrder) return order * 1
          if (productA[sortBy] === productB[sortBy]) return order * (_sortVal(productA, orderBy) - _sortVal(productB, orderBy))
          if (!sortOrder.includes(productA[sortBy]) && !sortOrder.includes(productB[sortBy])) return String(productA[sortBy]).localeCompare(String(productB[sortBy]))
          if (!sortOrder.includes(productA[sortBy])) return 1
          if (!sortOrder.includes(productB[sortBy])) return -1
          return (sortOrder.indexOf(productA[sortBy]) - sortOrder.indexOf(productB[sortBy]))
        })

      if (!groupBy) return { all: { sortedProducts: sorted } }
      const result = {}
      for (const product of sorted) {
        let groupValue = product[groupBy] ?? ''
        if (groupValue.length > 0 && typeof this.$store.getters[`somnambulistui/${groupBy}`] === 'function') {
          groupValue = this.$store.getters[`somnambulistui/${groupBy}`](groupValue) ?? ''
        }
        if (!result[groupValue]) result[groupValue] = { sortedProducts: [], label: groupValue }
        result[groupValue].sortedProducts.push(product)
      }

      const list = this.$store.state.somnambulistui[groupBy]
      const finalResult = {}
      if (list) {
        for (const [, value] of Object.entries(list)) {
          if (result[value]) finalResult[value] = result[value]
        }
      }

      if (result['']) finalResult[''] = result['']

      return finalResult
    },
    lightboxActions() {
      return [
        {
          label: 'Close',
          handler: () => {
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.showLightbox = false
            this.$emit('modalToggled')
          }
        }
      ]
    },
    substituteLightboxActions() {
      return [
        {
          label: 'Save Order',
          handler: () => {
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.showSubstituteLightbox = false
            this.$emit('modalToggled')
          }
        }
      ]
    },
    limitReached() {
      if (this.isFixedQuantity) return this.customOrderProductCount >= parseInt(this.getSelectedQuantityObject.customQuantity)
      const max = parseInt(this.getSelectedQuantityObject.customQuantityMax)
      if (max < 0) return false
      return this.customOrderProductCount >= max
    }
  },
  mounted() {
    Object.keys(this.selectedProducts).map((pid) => {
      let product = this.products.find(product => product.id === pid)
      if (!product) {
        this.setQuantity({ id: pid }, 0)
      }
    })
  },
  methods: {
    ...mapMutations('somnambulistui', ['INCREMENT_SELECTED_PRODUCT', 'DECREMENT_SELECTED_PRODUCT', 'SET_SELECTED_PRODUCT_COUNT', 'SET_SUBSTITUTE_PRODUCT']),
    onRequest(product) {
      return product.productaccess && product.productaccess.length !== 0 && !this.$store.getters['user/hasAnyRole'](product.productaccess.map(data => data.role))
    },
    hasDefaultSelection(product) {
      const selection = this.$store.state.somnambulistui.originalSelectedProducts[product.id]
      return selection && selection > 0
    },
    makeRequest(product) {
      const wishlist = this.getSelectedQuantityObject.wishlistform ?? product.wishlist
      return this.$eventHub.$emit('productRequestAdd', {
        mode: 'success',
        node: { ...product, wishlist },
        message: ''
      })
    },
    isVisible(product) {
      // If the product is unpublished, it is not visible
      if (product.status === 0) return false

      // If we are userID 1, product is visible
      if (this.$store.getters['user/userid'] === '1') return true

      // If we don't have any visibility settings, product is visible
      if (!product.visibility || product.visibility.length === 0) return true

      // If user has a visibility role, product is visible
      if (this.$store.getters['user/hasAnyRole'](product.visibility.map(data => data.role))) return true

      return false
    },
    getGroupByLabel(groupedProducts) {
      if (!('label' in groupedProducts)) return ''
      if (groupedProducts.label) return groupedProducts.label
      return 'Other'
    },
    keyPress(key) {
      if (isNaN(Number(key.key))) {
        key.preventDefault()
      }
    },
    getQuantity(product) {
      return this.selectedProducts[product.id] || 0
    },
    setQuantity(product, val) {
      val = Number(val)
      if (val > this.getQuantity(product)) {
        if (!this.maxReached(product)) {
          const customQuantity = parseInt(this.getSelectedQuantityObject.customQuantity) || 0
          const quantityRemaining = Math.min(customQuantity - this.customOrderProductCount + this.getQuantity(product), product.max_club)
          if (val > quantityRemaining) {
            this.SET_SELECTED_PRODUCT_COUNT({ id: product.id, quantity: quantityRemaining })
            return
          }
        }
        else {
          this.quantityInputKey += 1
          return
        }
      }
      this.SET_SELECTED_PRODUCT_COUNT({ id: product.id, quantity: val })
    },
    increment(product) {
      if (!this.maxReached(product)) {
        this.INCREMENT_SELECTED_PRODUCT(product.id)

        if (product.substitute_products && product.substitute_products.length) {
          if (product.substitute_products.length === 1) {
            return this.SET_SUBSTITUTE_PRODUCT({ id: product.id, products: product.substitute_products[0].product.id })
          }
          else if (!this.substituteProducts[product.id]) {
            this.showSubstituteLightBox(product)
          }
        }
      }
    },
    hasSettings(product) {
      return product.substitute_products && product.substitute_products.length && product.substitute_products.length > 1
    },
    showSubstituteLightBox(product) {
      this.substituteLightboxProduct = product
      this.showSubstituteLightbox = true
      /**
       * TODO: Fix inner modal position to allow z-index context
       * to apply for the product card modal, currently because the modal is inside the product card
       * the fixed checkout bar will always sit above it. Z-index has no effect
       * on elements in the dom within inner lays. This emission is
       * to toggle the z-index on a Stepper component.
       */
      this.$emit('modalToggled')
    },
    decrement(product) {
      this.DECREMENT_SELECTED_PRODUCT(product.id)
    },
    showDetails(product) {
      this.lightboxProduct = product
      this.currentLightboxImage = this.getFirstImage(product)
      this.showLightbox = true
      /**
       * TODO: Fix inner modal position to allow z-index context
       * to apply for the product card modal, currently because the modal is inside the product card
       * the fixed checkout bar will always sit above it. Z-index has no effect
       * on elements in the dom within inner lays. This emission is
       * to toggle the z-index on a Stepper component.
       */
      this.$emit('modalToggled')
    },
    getFirstImage(product) {
      if (product.images.length > 0) {
        return product.images[0].image || null
      }
      return null
    },
    price(product) {
      return `$${product.productsettings.price.toFixed(2)}`
    },
    discountedPrice(product) {
      let candidatePrice = product.productsettings.price
      if (this.$store.getters['somnambulistui/isDiscountTypeRoleAdjust']) {
        const adj = this.$store.getters['ecommerce/getTotalPriceAdjustment'](product)
        candidatePrice = product.productsettings.price + parseFloat(adj)
      }
      else if (this.$store.getters['somnambulistui/isDiscountTypeFixed']) {
        const adjRate = (1 - (this.getSelectedQuantityObject.discount / 100))
        candidatePrice = product.productsettings.price * adjRate
      }
      return `$${candidatePrice.toFixed(2)}`
    },
    priceDifferent(product) {
      return this.price(product) !== this.discountedPrice(product)
    },
    maxReached(product) {
      return this.limitReached || this.productMaxReached(product)
    },
    productMaxReached(product) {
      const quantity = this.getQuantity(product)
      const selection = this.$store.state.somnambulistui.originalSelectedProducts[product.id]
      return (product.max_club && quantity >= product.max_club) || ((!this.isVisible(product) || this.onRequest(product)) && quantity >= selection)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@scss/custom.scss';

.product-card {
  --quantity-selector-height: 4em;
}

.quantity-button-enter-active {
  transition: width 1s ease;
}
.quantity-button-enter {
  width: 0%;
}

.lightbox-product-image {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 100%;
    background-color: var(--main-product-image-background);
    margin: 0 0 1.5rem 0;
}
.lightbox-product-details  {
 ::v-deep .image-container-inner {
    position: relative;
    padding-bottom: 100%;

    >img {
      position: absolute;
      object-fit: contain;
      width: 100%;
      height: 100%;
      padding: 1.5em;
    }
  }
}
.lightbox-product-details {
   ::v-deep .pricing {
      display:flex;
      width:100%;
      margin:1.5rem 0;
      text-align: center;
      flex-wrap: nowrap;

      .price {
        font-size:1.2rem;
        @include media-breakpoint-up (md) {
        font-size:1.5rem;
        }
        display: flex;
        justify-content: center;
        align-items: center;
        justify-items: center;
        width:100%;
        &:not(:last-child){
          border-right:1px solid;
          border-color:var(--main-border-color);
        }
        &:first-child {
          padding-right:1.5rem;
          justify-content: flex-end;

        }
        &:last-child {
          padding-left:1.5rem;
          justify-content: flex-start;
        }
      }
      .sale-price{
        text-decoration: line-through;
      }
      .club-price {
        color: var(--main-highlight-text-color);
      }
    }
}
  .lightbox-product-details {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    .lightbox-product-title {
      margin:1.5rem  auto 1rem auto;
      font-size: 1.8em;
      color: var(--main-text-heading-color);
    }
    .lightbox-product-images {
      max-width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      padding: 0em 0em 2em 0em;
      .lightbox-product-images-slider {
        display: flex;
        justify-content: center;
        height: 100px;
        width: 100%;
        img {
          max-height: 100%;
          max-width: 100%;
          cursor: pointer;
          opacity: 0.8;
          @include hover-focus {
            opacity: 1;
          }
        }
      }

    }
    .lightbox-product-description {
      text-align:center;
      font-size: 1em;
      margin-bottom: 2em;
    }
    .lightbox-product-price {
      font-size: 1.2em;
      color: var(--main-text-color);
    }
  }
.product-cards {
  display:flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: -1em;
  width: calc(100% + 2em);
  .product-card{
    margin:.75em;
    width:100%;
    @include media-breakpoint-up (sm) {
     width: calc((100% / 2) - 1.5em);
    }
    @include media-breakpoint-up (md) {
     width: calc((100% / 3) - 1.5em);
    }
    @include media-breakpoint-up (lg) {
      width: calc((100% / 3) - 1.5em);
    }
  }

  .product-cards [class*="col-"] {
    margin-bottom: 2em;
  }
  .product-card {
    background-color: var(--main-card-background-color);
    padding: 1.5em;
    border-radius: var(--main-card-border-radius);
    box-shadow: var(--main-card-drop-shadow);
    margin-bottom:1.5em;
    position: relative;
    min-height:300px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    // overflow: hidden;
    .quantity-container {
      perspective: 300px;
      &.show-selector {
        .quantity-container-inner {
          transform: rotateX(90.01deg)
        }
      }
      margin-top: 2em;
      width: 100%;
      height: var(--quantity-selector-height);
      .quantity-container-inner {
        transform-origin: center center calc(-1 * var(--quantity-selector-height) / 2);
        height: var(--quantity-selector-height);
        transition: transform 0.3s;
        width: 100%;
        position: relative;
        transform-style: preserve-3d;
        .add-to-order {
          opacity: 1;
          z-index: 4;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: var(--quantity-selector-height);
          padding: 0 1.5em;
          font-size: 1em;
          display: flex;
          justify-content: center;
          align-items: center;
          overflow: hidden;
          white-space: nowrap;
        }
        .on-request {
          opacity: 1;
          z-index: 4;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: var(--quantity-selector-height);
          padding: 0 1.5em;
          font-size: 1em;
          display: flex;
          justify-content: center;
          align-items: center;
          overflow: hidden;
          white-space: nowrap;
        }
        .quantity-selector {
          transform-origin: bottom center;
          transform: translateZ(calc(-1 * var(--quantity-selector-height))) rotate3d(1, 0, 0, -90deg);
          z-index: 3;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          display: flex;
          justify-content: center;
          height: var(--quantity-selector-height);
          .btn {
            opacity: 1;
            padding-left:1.5rem;
            padding-right:1.5rem;
            font-size: 1.5em;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .quantity-input {
            input {
              border-radius: 0%;
              border:0px;
              height:100%;
              background: #ebebeb;
              text-align: center;
              font-size: 1.5em;
            }
          }
          .decrement {
            border-radius: 3px 0px 0px 3px;
          }
          .increment {
            border-radius: 0px 3px 3px 0px;
          }
        }
      }
    }
    .product-max-reached {
      background: var(--main-link-color);
      color: var(--main-text-color);
      font-size: 1.1em;
      width: 100%;
      text-align: center;
      padding: 1em;
      word-break: break-all;
      word-break: break-word;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 5;
      box-shadow: 0px 1px 8px black;
    }
    .product-not-readdable {
      background: var(--main-link-color);
      color: var(--main-text-color);
      font-size: 1.1em;
      width: 100%;
      text-align: center;
      padding: 1em;
      word-break: break-all;
      word-break: break-word;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 5;
      box-shadow: 0px 1px 8px black;
    }
    .product-image {
      z-index: 1;
      width: 50%;
      margin-bottom: 1em;
      position: relative;
      overflow: hidden;
      cursor: pointer;
      .award-winner {
        border: 1px solid var(--main-border-color);
        border-radius: 50% 50%;
        width: 5em;
        height: 5em;
        margin-left: -2.5em;
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        position: absolute;
        font-size: 0.7em;
        background: var(--main-award-badge-background-color);
        color: var(--main-award-badge-text-color);
        bottom: 0;
        left: 50%;
        z-index: 2;
      }
      ::v-deep .details-icon {
        opacity: 0;
        pointer-events: none;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 4;
        transition: opacity .3s;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
        svg {
          width: 2em;
          height: 2em;
          path {
            fill: rgba(255, 255, 255, 0.8);
          }
        }
      }
      &::after {
        content: '';
        transition: opacity .3s;
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        opacity: 0;
        pointer-events: none;
        background: rgba(0, 0, 0, 0.8);
        z-index: 3;
      }
      &:hover {
        &::after {
          opacity: 1;
        }
        .details-icon {
          opacity: 1;
        }
      }
    }
    .product-quantity {
      z-index: 2;
      position: absolute;
      top: 1.5em;
      left: 1.5em;
      font-size: 1.1em;
      width: 3em;
      height: 3em;
      border-radius: 100%;
      background-color: var(--main-badge-background-color);
      color: var(--main-badge-text-color);
      align-items: center;
      align-content: center;
      display: flex;
      justify-content: center;
      z-index: 2;
    }
    .product-info {
      z-index: 3;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      text-align: center;
      flex-grow: 1;
      .product-title {
        h3 {
          font-size: 1.5em;
        }
      }
    }

    .pricing {
      display:flex;
      margin-top:1rem;
      text-align: center;
      flex-wrap: nowrap;

      .price {
        font-size:1.2rem;
        @include media-breakpoint-up (md) {
        font-size:1.1rem;
        }
        @include media-breakpoint-up (xl) {
        font-size:1.45rem;
        }
        display: flex;
        justify-content: center;
        align-items: center;
        justify-items: center;
        width:100%;
        &:not(:last-child){
          border-right:1px solid;
          border-color:var(--main-border-color);
        }
        &:first-child {
          padding-right:.5rem;
        }
        &:last-child {
          padding-left:.5rem;
        }
      }
      .sale-price{
        text-decoration: line-through;
      }
      .club-price {
        color: var(--main-highlight-text-color);
      }
    }
  }
}

.move-in-enter-active, .move-in-leave-active {
  transition: transform .5s ease-in;
}
.move-in-enter, .move-in-leave-to {
  transform: translateY(-100%);
}

.settings-icon {
  margin-left: auto;
}

.lightbox-product-details {
  .description {
    margin-bottom: 1em;
  }
  .product {
    background-color: var(--edit-page-content-item-background-color);
    border-radius: 6px 6px 6px 6px;
    box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.25);
    padding: 1.5em;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2em;
    // disable highlight
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently supported by Chrome and Opera */
    .product-title {
      font-size: 1.1em;
      font-weight: 600;

      &:hover {
        cursor: pointer;
      }
    }
  }
}

</style>
